import { VENMO_AUTOMATION_STAGE_STATUSES } from './venmoAutomationStageStatuses'
import * as venmoUrls from './venmoUrls'
import { VenmoSettingKey } from '../../recommendations/recommendationSettingsKeys'
import * as enums from '../../utils/enums'
import { VenmoRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import * as promises from '../interruptiblePromises'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
  getCurrentValueFnFromKey,
  getIsFulfilledFnFromKey,
} from '../recommendationCardDisplayUtils'
import { getElement } from '../utils'
import { evaluateXpath, getCaseInsensitiveAttribute } from '../xpath'

// Settings properties
const getCurrentValueFn = (key) =>
  getCurrentValueFnFromKey(key, (value) => (value ? value.join(', ') : ''))

const getIsFulfilledFn = (key) =>
  getIsFulfilledFnFromKey(key, (value) => (value ? value.length === 0 : true))

const getCantUndoFn = () => defaultCantUndoFn

const getIsDisabledFn = () => defaultIsDisabledFn

const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

const notificationLabels = {
  [VenmoSettingKey.NOTIFICATION_PAYMENT_REQUEST]: 'Payment requests',
  [VenmoSettingKey.NOTIFICATION_PAYMENT_RECEIVED]: 'Payments received',
  [VenmoSettingKey.NOTIFICATION_PAYMENT_SENT]: 'Payments sent',
  [VenmoSettingKey.NOTIFICATION_ACTIVITY_MENTIONS]: 'Activity on your mentions',
  [VenmoSettingKey.NOTIFICATION_COMMENTS_ON_PAYMENTS]: 'Comments on payments',
  [VenmoSettingKey.NOTIFICATION_LIKES_ON_PAYMENTS]: 'Likes on payments',
  [VenmoSettingKey.NOTIFICATION_MENTIONS_OF_YOU]: 'Mentions of you',
  [VenmoSettingKey.NOTIFICATION_VENMO_PURCHASES]: 'Purchases',
  [VenmoSettingKey.NOTIFICATION_VENMO_UPDATES]: 'Venmo updates',
}

const notificationChannelsToDisable = {
  [VenmoSettingKey.NOTIFICATION_PAYMENT_REQUEST]: ['Text', 'Push'],
  [VenmoSettingKey.NOTIFICATION_PAYMENT_RECEIVED]: ['Text', 'Push'],
  [VenmoSettingKey.NOTIFICATION_PAYMENT_SENT]: ['Text', 'Push'],
  [VenmoSettingKey.NOTIFICATION_ACTIVITY_MENTIONS]: ['Push'],
  [VenmoSettingKey.NOTIFICATION_COMMENTS_ON_PAYMENTS]: ['Text', 'Push'],
  [VenmoSettingKey.NOTIFICATION_LIKES_ON_PAYMENTS]: ['Email', 'Push'],
  [VenmoSettingKey.NOTIFICATION_MENTIONS_OF_YOU]: ['Push'],
  [VenmoSettingKey.NOTIFICATION_VENMO_PURCHASES]: ['Push'],
  [VenmoSettingKey.NOTIFICATION_VENMO_UPDATES]: ['Email', 'Push'],
}

// DOM helpers
async function getNotificationCheckbox(
  notificationType: string,
  notificationChannel: string,
): Promise<HTMLInputElement | null> {
  if (!notificationType || !notificationChannel) {
    return null
  }

  const checkboxXPath = `//input[contains(${getCaseInsensitiveAttribute('aria-label')}, "${notificationType.toLowerCase()} ${notificationChannel.toLowerCase()}")]`
  const checkbox =
    await promises.getWaitForElementToAppearPromise<HTMLInputElement>(
      document,
      (node) => evaluateXpath<HTMLInputElement>(node, checkboxXPath)[0],
      3000,
    )

  return checkbox
}

async function saveNotificationSettings(): Promise<void> {
  const saveButton = await getElement<HTMLButtonElement>(
    document,
    `//button[` +
      `  @type="submit" and ` +
      `  descendant-or-self::*[contains(text(), "Save")]` +
      `]`,
  )

  saveButton.click()
}

// Setup gather stage definitions

enum StageKey {
  GATHER_SETTING_NOTIFICATIONS = 'GATHER_SETTING_NOTIFICATIONS',
  UPDATE_SETTING_NOTIFICATIONS = 'UPDATE_SETTING_NOTIFICATIONS',
  UNDO_SETTING_NOTIFICATIONS = 'UNDO_SETTING_NOTIFICATIONS',
}

const gatherStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.GATHER_SETTING_NOTIFICATIONS,
    status: VENMO_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    url: venmoUrls.notificationsUrl,
    action: async (machineContext, userContext) => {
      const { updateStoredInitialValues } = userContext

      for (const setting of Object.keys(notificationLabels)) {
        const gatheredValues = []
        for (const notificationType of notificationChannelsToDisable[setting]) {
          const checkbox = await getNotificationCheckbox(
            notificationLabels[setting],
            notificationType,
          )
          if (checkbox?.checked) {
            gatheredValues.push(notificationType)
          }
        }
        await updateStoredInitialValues(
          {
            [setting]: gatheredValues,
          },
          VenmoRecommendationKey.VENMO_NOTIFICATIONS,
        )
      }
    },
  },
]

const updateStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UPDATE_SETTING_NOTIFICATIONS,
    status: VENMO_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    url: venmoUrls.notificationsUrl,
    action: async (machineContext, userContext) => {
      const { settingsSelected, isDry } = userContext
      // Skip if not selected
      if (!settingsSelected) {
        return null
      }

      for (const setting of Object.keys(notificationLabels)) {
        if (settingsSelected[setting]) {
          for (const notificationType of notificationChannelsToDisable[
            setting
          ]) {
            const checkbox = await getNotificationCheckbox(
              notificationLabels[setting],
              notificationType,
            )

            if (checkbox?.checked && !isDry) {
              checkbox.click()
            }
          }
        }
      }

      return saveNotificationSettings()
    },
  },
]

const undoStageDefs: StageDef[] = [
  {
    type: enums.StageType.ACTION,
    key: StageKey.UNDO_SETTING_NOTIFICATIONS,
    status: VENMO_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    url: venmoUrls.notificationsUrl,
    action: async (machineContext, userContext) => {
      const { initialValues, isDry } = userContext

      // Check current setting

      // Toggle setting if checkbox does not match initial values
      if (initialValues) {
        for (const setting of Object.keys(notificationLabels)) {
          if (initialValues[setting]) {
            for (const notificationType of initialValues[setting]) {
              const checkbox = await getNotificationCheckbox(
                notificationLabels[setting],
                notificationType,
              )

              if (!checkbox?.checked && !isDry) {
                checkbox.click()
              }
            }
          }
        }

        return saveNotificationSettings()
      }
      return null
    },
  },
]

export {
  getCantUndoFn,
  getCurrentValueFn,
  getIsDisabledFn,
  getIsDisabledReasonFn,
  getIsFulfilledFn,
  gatherStageDefs,
  updateStageDefs,
  undoStageDefs,
}
